import {type FormEvent, useCallback, useEffect, useRef, useState} from 'react';
import {useMaskito} from '@maskito/react/';
import {type MaskitoOptions} from '@maskito/core';
import {useIntl, FormattedMessage} from 'react-intl';
import {mergeRefs, useId} from '@react-aria/utils';
import classNames from 'classnames';

import {Input} from '@time-webkit/all/atoms/input';
import {Button, BUTTON_SIZE} from '@time-webkit/all/atoms/button';
import {FormError} from '@time-webkit/all/atoms/form-error';
import {Alert, AlertType} from '@time-webkit/all/atoms/alert';
import {Heading, HeadingLevel} from '@time-webkit/all/atoms/heading';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {useCountdown} from 'usehooks-ts';

import {useDisappearingError} from '@time-webkit/all/hooks/use_disappearing_error';

import styles from './totp_confirmation.module.css';

export type TOTPVerificationResult = {status: 'LIMIT_LOCKED' | 'INVALID'} | null

type Props = {
    nextAttemptSeconds: number;
    serverError: boolean;
    verificationResult?: TOTPVerificationResult;
    onSubmit?: (code: string) => unknown;
    shouldDisableInput?: boolean;
};

const INPUT_MASK = [/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/];
const CLEAR_ERROR_TIMEOUT_MS = 1_000;

const maskitoOptions: MaskitoOptions = {
    mask: INPUT_MASK,
};

const maskedCodeToNumberCode = (maskedCode: string) => maskedCode.replace(' ', '');

export const TOTPConfirmationForm = ({
    onSubmit,
    serverError,
    verificationResult,
    nextAttemptSeconds,
    shouldDisableInput = false,
}: Props) => {
    const inputDescriptionId = useId();
    const {formatMessage} = useIntl();
    const [bodyMTypography] = useTypography([{size: TypographySize.BodyM}, {size: TypographySize.BodyL}]);
    const inputRef = useMaskito({options: maskitoOptions});
    const [error, setError] = useDisappearingError();
    const [code, setCode] = useState('');
    const [hasCounterFinished, setHasCounterFinished] = useState(false);
    const [count, {startCountdown, resetCountdown}] = useCountdown({countStart: nextAttemptSeconds});

    useEffect(() => {
        if (verificationResult?.status === 'LIMIT_LOCKED') {
            if (count === 0) {
                setHasCounterFinished(true);
                setError(null);
                return;
            }

            setError(formatMessage({
                id: 'auths.totp_error.limit_locked',
                defaultMessage: 'You\'ve exceeded the limit of login attempts, try again in {minutes, plural, one {1 minute} other {# minutes}}',
            }, {minutes: Math.ceil(count / 60)}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count]);

    const innerInputRef = useRef<HTMLInputElement>(null);

    const shouldDisableForm = (verificationResult?.status === 'LIMIT_LOCKED' && !hasCounterFinished) ||
        verificationResult?.status === 'INVALID' || shouldDisableInput;

    const handleSubmit = useCallback(
        async (event: FormEvent) => {
            event.preventDefault();

            if (shouldDisableForm) {
                return;
            }
            if (code.length === 0) {
                setError(formatMessage({
                    id: 'signup.email_guest.confirm_phone_no_code',
                    defaultMessage: 'Enter the code',
                }));
                return;
            }
            onSubmit?.(maskedCodeToNumberCode(code));
        },
        [shouldDisableForm, code, onSubmit, setError, formatMessage],
    );

    const handleChange = (newValue: string) => {
        if (newValue.length === INPUT_MASK.length) {
            onSubmit?.(maskedCodeToNumberCode(newValue));
        }
        if (error) {
            setError(null);
        }
        setCode(newValue);
    };

    const handleFocus = useCallback(() => {
        setError(null);
    }, [setError]);

    useEffect(() => {
        if (verificationResult === null) {
            innerInputRef.current?.focus();
        }
    }, [verificationResult]);

    useEffect(() => {
        if (verificationResult?.status === 'LIMIT_LOCKED') {
            setHasCounterFinished(false);
            resetCountdown();
            startCountdown();
            setCode('');
        } else if (verificationResult?.status === 'INVALID') {
            const onDisappear = () => {
                setCode('');
                innerInputRef.current?.focus();
            };
            setError(formatMessage({
                id: 'auths.totp.description',
                defaultMessage: 'Sent via an one-time password service',
            }), CLEAR_ERROR_TIMEOUT_MS, onDisappear);
        } else {
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setError, verificationResult]);

    return (
        <form
            noValidate={true}
            className={styles.form}
            onSubmit={handleSubmit}
        >
            <Heading
                className={styles.title}
                level={HeadingLevel.FIFTH}
            >
                <FormattedMessage
                    id={'auths.totp.header'}
                    defaultMessage={'Enter the code'}
                />
            </Heading>
            <div className={styles.field}>
                <Input
                    aria-label=''
                    aria-describedby={inputDescriptionId}
                    aria-errormessage={inputDescriptionId}
                    autoFocus={true}
                    disabled={shouldDisableForm}
                    placeholder='___ ___'
                    error={Boolean(error)}
                    label={formatMessage({
                        id: 'auths.totp.field_label',
                        defaultMessage: 'Confirmation code',
                    })}
                    value={code}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    ref={mergeRefs(innerInputRef, inputRef)}
                />
                <div
                    className={classNames(styles.description, bodyMTypography)}
                    id={inputDescriptionId}
                >
                    {error ? (
                        <FormError
                            show={true}
                            message={error}
                        />
                    ) : (
                        <div className={classNames({[styles.error_color]: Boolean(error)})}>
                            <FormattedMessage
                                id='auths.totp.description'
                                defaultMessage={'Sent via an one-time password service'}
                            />
                        </div>
                    )}
                </div>
            </div>
            {serverError && (
                <Alert
                    className={styles.error}
                    type={AlertType.Danger}
                    size='medium'
                    secondaryMessage={formatMessage({id: 'auths.error.general', defaultMessage: 'Something went wrong, try again later'})}
                />
            )}
            <Button
                type='submit'
                size={BUTTON_SIZE.LARGE}
            >
                <FormattedMessage
                    id='auths.totp.submit_button_text'
                    defaultMessage={'Continue'}
                />
            </Button>
        </form>
    );
};
